import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/PaymentIntentsService';
import {PaymentIntent} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const usePaymentIntent = (paymentIntentId?: string) => {
    const queryClient = useQueryClient();

    const paymentIntentQuery = useQuery({
        queryKey: ['paymentIntent', paymentIntentId],
        queryFn: () => fetch(paymentIntentId!),
        enabled: !!paymentIntentId,
    });

    const createMutation = useMutation({
        mutationFn: (values: PaymentIntent) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            return add(payload as PaymentIntent);
        },
        onSuccess: () => {
            notifySuccess('Payment intent created');
            queryClient.invalidateQueries({queryKey: ['paymentIntents']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: PaymentIntent) => {
            if (!paymentIntentId) throw new Error('paymentIntentId is required parameter');
            return update(paymentIntentId, values);
        },
        onSuccess: () => {
            notifySuccess('Payment intent updated');
            queryClient.invalidateQueries({queryKey: ['paymentIntents']});
            queryClient.invalidateQueries({queryKey: ['paymentIntent', paymentIntentId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!paymentIntentId) throw new Error('paymentIntentId is required parameter');
            return deleteRecord(paymentIntentId);
        },
        onSuccess: () => {
            notifySuccess('Payment intent deleted');
            queryClient.invalidateQueries({queryKey: ['paymentIntents']});
        },
        onError: notifyError,
    });

    return {
        record: paymentIntentQuery.data,
        loading: paymentIntentQuery.isLoading,
        fetchRecord: paymentIntentQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
